/* eslint-disable react/prop-types */
import { useCallback, useMemo } from "react";
import { t } from "ttag";
import * as Yup from "yup";

import FormCollectionPicker from "metabase/collections/containers/FormCollectionPicker";
import { useGetDefaultCollectionId } from "metabase/collections/hooks";
import type { FilterItemsInPersonalCollection } from "metabase/common/components/EntityPicker";
import { FormFooter } from "metabase/common/components/FormFooter";
import { Dashboards } from "metabase/entities/dashboards";
import {
  Form,
  FormProvider,
  FormTextInput,
  FormSubmitButton,
  FormTextarea,
  FormErrorMessage,
} from "metabase/forms";
import * as Errors from "metabase/lib/errors";
import { useDispatch } from "metabase/lib/redux";
import { Button } from "metabase/ui";
import type { CollectionId, Dashboard } from "metabase-types/api";

const SAVE_DASHBOARD_VIEW_SCHEMA = Yup.object({
  name: Yup.string()
    .required(Errors.required)
    .max(100, Errors.maxLength)
    .default(""),
  description: Yup.string().nullable().max(255, Errors.maxLength).default(null),
  collection_id: Yup.number().nullable(),
});

export interface CreateDashboardProperties {
  name: string;
  description: string | null;
  collection_id: CollectionId;
}

export interface CreateDashboardFormOwnProps {
  collectionId?: CollectionId | null;
  onCreate?: (dashboard: Dashboard) => void;
  onClose?: () => void;
  initialValues?: CreateDashboardProperties | null;
  filterPersonalCollections?: FilterItemsInPersonalCollection;
}

function SaveDashboardViewForm({
  collectionId,
  onClose,
  initialValues,
  onCreate,
  filterPersonalCollections,
}: CreateDashboardFormOwnProps) {
  const dispatch = useDispatch();
  const initialCollectionId = useGetDefaultCollectionId(collectionId);
  const dashboardUrl = window.location.href;

  const computedInitialValues = useMemo(
    () => ({
      ...SAVE_DASHBOARD_VIEW_SCHEMA.getDefault(),
      collection_id: initialCollectionId,
      saved_view: dashboardUrl,
      ...initialValues,
    }),
    [initialCollectionId, initialValues, dashboardUrl],
  );

  const handleCreate = useCallback(
    async (values: CreateDashboardProperties) => {
      const action = await dispatch(Dashboards.actions.create(values));
      const dashboard = Dashboards.HACK_getObjectFromAction(action);
      onCreate?.(dashboard);
    },
    [dispatch, onCreate],
  );

  return (
    <FormProvider
      initialValues={computedInitialValues}
      validationSchema={SAVE_DASHBOARD_VIEW_SCHEMA}
      onSubmit={handleCreate}
    >
      <Form>
        <FormTextInput
          name="name"
          label={t`Name`}
          mb="1rem"
          placeholder={t`What is the name of your view?`}
          autoFocus
        />
        <FormTextarea
          name="description"
          label={t`Description`}
          mb="1rem"
          placeholder={t`It's optional but oh, so helpful`}
          minRows={5}
          nullable
        />
        <FormCollectionPicker
          name="collection_id"
          title={t`Which collection should this go in?`}
          filterPersonalCollections={filterPersonalCollections}
        />
        <FormFooter>
          <FormErrorMessage inline />
          <Button onClick={onClose}>{t`Cancel`}</Button>
          <FormSubmitButton label={t`Create`} />
        </FormFooter>
      </Form>
    </FormProvider>
  );
}

// eslint-disable-next-line import/no-default-export -- deprecated usage
export default SaveDashboardViewForm;
